import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  CheckCircleIcon,
  PhotoIcon,
  DocumentTextIcon,
  XMarkIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBone,
  faBriefcaseMedical,
  faPersonWalking,
  faPaw,
} from "@fortawesome/free-solid-svg-icons";
import PetDetailsHeader from "./petDetailsHaeder";
import SelectAddOnsDrawer from "../Orders/SelectAddOnsDrawer";
import ProductCatalog from "../../components/common/ProductCatalog";
import OtherChargesDrawer from "../../components/common/OtherChargesDrawer";
import ServiceDetailsCard from "./ServiceDetailsCard";
import { httpService } from "../../services/httpService";
import FileUpload from "../../components/common/FileUpload";
import Swal from "sweetalert2";
import GroomingStartedDrawer from "../services/GroomingStartedDrawer";
import VetClinicStartedDrawer from "../services/VetClinicStartedDrawer";

const TIME_SLOTS = ["Morning", "Afternoon", "Evening"];

const ACTIVITY_TYPE_ORDER = {
  feeding: 1,
  walking: 2,
  medication: 3,
  custom: 4,
  activity: 5,
};

const getActivityIcon = (type) => {
  switch (type?.toLowerCase()) {
    case "feeding":
      return faBone;
    case "medication":
      return faBriefcaseMedical;
    case "walking":
      return faPersonWalking;
    default:
      return faPaw;
  }
};

const isRelatedBookingCompleted = (booking) => {
  const status = String(booking?.status || booking?.originalItem?.status || "")
    .trim()
    .toLowerCase();

  return ["completed", "complete", "closed", "checkout", "checked_out"].includes(
    status,
  );
};

const getBookingTimeSlot = (startTime) => {
  if (!startTime) return null;

  const match = String(startTime)
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3].toUpperCase();

  if (period === "AM") {
    if (hour === 12) hour = 0;
  } else {
    if (hour !== 12) hour += 12;
  }

  const totalMinutes = hour * 60 + minute;

  if (totalMinutes < 12 * 60) {
    return "Morning";
  }

  if (totalMinutes < 17 * 60) {
    return "Afternoon";
  }

  return "Evening";
};

const normalizeBoardingActivities = (schedule = []) => {
  const grouped = {};

  if (!Array.isArray(schedule)) {
    console.warn("Expected schedule array but received:", schedule);
    return grouped;
  }

  schedule.forEach((day) => {
    const dateKey = day?.date;

    if (!dateKey) {
      return;
    }

    grouped[dateKey] = {
      Morning: [],
      Afternoon: [],
      Evening: [],
    };

    const slots = day?.slots || {};

    Object.entries(slots).forEach(([apiSlot, slotActivities]) => {
      if (!Array.isArray(slotActivities)) {
        return;
      }

      const normalizedSlot = String(apiSlot).trim().toLowerCase();

      let uiSlot = null;

      if (normalizedSlot === "morning") {
        uiSlot = "Morning";
      } else if (normalizedSlot === "afternoon" || normalizedSlot === "noon") {
        uiSlot = "Afternoon";
      } else if (normalizedSlot === "evening") {
        uiSlot = "Evening";
      }

      if (!uiSlot) {
        console.warn("Unknown boarding activity time slot:", apiSlot);
        return;
      }

      const normalizedTasks = slotActivities.map((activity, index) => {
        let label =
          activity?.description || activity?.scheduled_task_type || "Activity";

        if (activity?.is_custom) {
          label = activity?.description || "Custom Activity";
        }

        let note = activity?.notes || "";

        if (note === label) {
          note = "";
        }

        return {
          id:
            activity?._id ||
            `${activity?.scheduled_task_type}-${dateKey}-${apiSlot}-${index}`,
          type: activity?.scheduled_task_type || "activity",
          label: label,
          note: note,
          details: "",
          checked: Boolean(activity?.is_completed),
          photos: activity?.photos || [],
          originalActivity: activity,
        };
      });

      grouped[dateKey][uiSlot].push(...normalizedTasks);
    });
  });

  return grouped;
};

const BoardingStartedDrawer = ({ open, booking, onClose, appointmentRow, onComplete }) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  const [taskStatus, setTaskStatus] = useState({});
  const [extraTasks, setExtraTasks] = useState({});
  const [activities, setActivities] = useState({
    Morning: [],
    Afternoon: [],
    Evening: [],
  });
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [activitiesError, setActivitiesError] = useState("");
  const [isAddActivityModalOpen, setIsAddActivityModalOpen] = useState(false);
  const [addActivityType, setAddActivityType] = useState("custom");
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [headerRefreshKey, setHeaderRefreshKey] = useState(0);

  const customer = booking?.customer || {};
  const service = booking?.service || {};
  const raw = appointmentRow || {};
  const serviceRefId =
    raw?.service_ref_id ||
    raw?.ref_id ||
    booking?.service_ref_id ||
    booking?.service?.service_ref_id ||
    booking?.booking_id ||
    "";
  const [addons, setAddons] = useState([]);
  const [products, setProducts] = useState([]);
  const [otherCharges, setOtherCharges] = useState([]);
  const [isAddonsDrawerOpen, setIsAddonsDrawerOpen] = useState(false);
  const [isProductsDrawerOpen, setIsProductsDrawerOpen] = useState(false);
  const [isOtherChargesDrawerOpen, setIsOtherChargesDrawerOpen] =
    useState(false);

  const clientName =
    customer.name ||
    `${raw.client_first_name || ""} ${raw.client_last_name || ""}`.trim() ||
    "—";
  const clientPhone = customer.phone || raw.phone || "";
  const clientEmail = customer.email || raw.email || "";
  const clientId = booking?.pet?.vendor_client_id || raw.vendor_client_id || "";

  const staffName = service.staffName || raw.employee_name || "";
  const orderId = booking?.order_id || raw.order_id || "";
  const vendorPetId =
    booking?.pet?.vendor_pet_id || raw.vendor_pet_id || raw.pet_id || "";

  const startDateRaw =
    booking?.start_date || raw?.start_date || new Date().toISOString();
  const endDateRaw =
    booking?.end_date || raw?.end_date || new Date().toISOString();

  const [serviceDetails, setServiceDetails] = useState({
    name: service.service_name || raw.service_name || "",
    duration: service.duration || raw.duration || "",
    date: service.start_date || raw.start_date || "",
    time: service.start_time || raw.start_time || "",
    price:
      service.total_price || raw.total_price || raw.unit_price_with_tax || 0,
  });
  const [boardingOrderItem, setBoardingOrderItem] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderLoading, setOrderLoading] = useState(false);
  const [selectedRelatedBooking, setSelectedRelatedBooking] = useState(null);
  const [isGroomingDrawerOpen, setIsGroomingDrawerOpen] = useState(false);
  const [isVetDrawerOpen, setIsVetDrawerOpen] = useState(false);
  
  const [issingleserviceOrder, setIsSingleServiceOrder] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(
    () => booking?.status || raw?.status || "",
  );
  const isServiceCompleted = [
    "completed",
    "complete",
    "closed",
    "checkout",
    "checked_out",
  ].includes(String(serviceStatus || "").toLowerCase().trim());
  const [orderType, setOrderType] = useState(null);

  const fetchOrder = async () => {
    if (!orderId) {
      console.warn("No orderId available for fetching order");
      return;
    }

    try {
      setOrderLoading(true);

      const res = await httpService.get(
        `/vendororder/getvendororder/${orderId}`,
      );

      if (res?.code === 200 && res?.data) {
        const orderData = res.data;
        setOrderType(orderData.order_type);

        const orderItems = Array.isArray(orderData.order_items)
          ? orderData.order_items
          : [];
          if(orderItems.length == 1)
            setIsSingleServiceOrder(true);
          else
            setIsSingleServiceOrder(false);

        setOrderItems(orderItems);

        // Find the order item belonging to the current boarding service/pet
        const matchedItem =
          orderItems.find(
            (item) =>
              item?.ref_id === serviceRefId &&
              item?.vendor_pet_id === vendorPetId,
          ) ||
          orderItems.find(
            (item) =>
              item?.vendor_pet_id === vendorPetId &&
              item?.item_type === "boarding",
          ) ||
          orderItems.find(
            (item) =>
              item?.service_id === raw?.service_id &&
              item?.vendor_pet_id === vendorPetId,
          ) ||
          orderItems.find(
            (item) =>
              item?.item_type === "boarding" &&
              item?.service_id === raw?.service_id,
          );

        if (matchedItem) {
          console.log("Matched boarding order item:", matchedItem);

          setBoardingOrderItem(matchedItem);
          if (matchedItem.status) setServiceStatus(matchedItem.status);
          else if (res.data.status) setServiceStatus(res.data.status);

          // ✅ ADD THIS: Restore addons, products, and other charges from the matched item
          if (matchedItem.addons) setAddons(matchedItem.addons);
          if (matchedItem.products) setProducts(matchedItem.products);
          if (matchedItem.otherCharges)
            setOtherCharges(matchedItem.otherCharges);

          // Also keep serviceDetails in sync with the order API
          setServiceDetails((prev) => ({
            ...prev,
            price_surcharge: matchedItem?.price_surcharge || [],
            discount_amount: matchedItem?.discount_amount || 0,
            gst_amount: matchedItem?.gst_amount || 0,
            base_total: matchedItem?.base_total || 0,
            quantity: matchedItem?.quantity || 1,
            name: matchedItem.service_name || prev.name,
            duration:
              matchedItem.duration !== undefined &&
              matchedItem.duration !== null
                ? matchedItem.duration
                : prev.duration,
            date: matchedItem.start_date || prev.date,
            time: matchedItem.start_time || prev.time,
            price:
              matchedItem.total_price ||
              matchedItem.final_total ||
              matchedItem.price ||
              prev.price,
            checkInDate: matchedItem.start_date || "",
            checkOutDate: matchedItem.end_date || "",
            durationDays: matchedItem.duration || "",
          }));
        } else {
          console.warn("Could not find matching boarding order item", {
            orderId,
            serviceRefId,
            vendorPetId,
            serviceId: raw?.service_id,
            orderItems,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching order:", error);
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !orderId) {
      return;
    }

    fetchOrder();
  }, [open, orderId, serviceRefId, vendorPetId]);

  useEffect(() => {
    if (!open) return;
    setServiceStatus(booking?.status || raw?.status || "");
  }, [open]);

  const serviceCategoryTypeId =
    raw.service_category_type_id ||
    raw.service_type_id ||
    service.service_category_type_id;

  const serviceCategory =
    service.service_category_name ||
    raw.service_category_name ||
    raw.service_type_name ||
    "";

  const dateTabs = useMemo(() => {
    const dates = [];
    const current = new Date(startDateRaw);
    const end = new Date(endDateRaw);

    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (end < current) return [new Date(current)];

    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [startDateRaw, endDateRaw]);

  const getDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const relatedBookings = useMemo(() => {
    if (!Array.isArray(orderItems) || !vendorPetId) {
      return [];
    }

    return orderItems
      .filter((item) => {
        // Only bookings belonging to the same pet
        if (item?.vendor_pet_id !== vendorPetId) {
          return false;
        }

        // Do not show the current boarding booking itself
        if (item?.item_type === "boarding") {
          return false;
        }

        // Only actual service bookings
        return Boolean(item?.start_date);
      })
      .map((item, index) => {
        const dateKey = String(item.start_date).slice(0, 10);
        const timeSlot = getBookingTimeSlot(item.start_time);

        if (!timeSlot) {
          return null;
        }

        return {
          id: `related-booking-${item._id || index}`,
          dateKey,
          timeSlot,
          type: item.item_type || "service",
          serviceName: item.service_name || "Service",
          startTime: item.start_time || "",
          duration: item.duration || "",
          employeeName: item.employee_name || "",
          status: item.status || "",
          originalItem: item,
        };
      })
      .filter(Boolean);
  }, [orderItems, vendorPetId]);

  const handleOpenRelatedBooking = (booking) => {
    setSelectedRelatedBooking(booking);

    const type = String(booking?.type || "").toLowerCase();

    if (type === "grooming") {
      setIsGroomingDrawerOpen(true);
    } else if (
      type === "vet" ||
      type === "vet_clinic" ||
      type === "veterinary"
    ) {
      setIsVetDrawerOpen(true);
    }
  };

  const handleCloseGroomingDrawer = () => {
    setIsGroomingDrawerOpen(false);
    setSelectedRelatedBooking(null);
  };

  const handleCloseVetDrawer = () => {
    setIsVetDrawerOpen(false);
    setSelectedRelatedBooking(null);
  };

  const getRelatedDrawerBooking = (relatedBooking) => {
    const item = relatedBooking?.originalItem || {};

    return {
      order_id: orderId,

      booking_id: item.ref_id || item._id || "",

      status: item.status || "upcoming",

      customer: {
        name:
          `${item.client_first_name || clientName || ""} ${
            item.client_last_name || ""
          }`.trim() || clientName,
        phone: item.phone || clientPhone || "",
        email: item.email || clientEmail || "",
      },

      pet: {
        vendor_pet_id: item.vendor_pet_id || vendorPetId || "",
        vendor_client_id: item.vendor_client_id || clientId || "",
        name: item.pet_name || raw.pet_name || "",
        breed: item.pet_breed || raw.pet_breed || "",
        size: item.pet_size || raw.pet_size || "",
        type: item.pet_type || raw.pet_type || "",
      },

      service: {
        service_name: item.service_name || relatedBooking?.serviceName || "",
        duration: item.duration || relatedBooking?.duration || "",
        start_date: item.start_date || "",
        start_time: item.start_time || "",
        staffName: item.employee_name || relatedBooking?.employeeName || "",
        total_price:
          item.total_price || item.unit_price_with_tax || item.price || 0,
      },

      pricing: {
        total: item.total_price || item.unit_price_with_tax || item.price || 0,
      },
    };
  };

  // ── Add-ons ──────────────────────────────────────────────
  const openAddonsDrawer = () => {
    if (isServiceCompleted) return;
    setIsAddonsDrawerOpen(true);
  };

  const closeAddonsDrawer = () => setIsAddonsDrawerOpen(false);

  const handleSaveAddons = async (updatedAddons) => {
    setIsAddonsDrawerOpen(false);

    if (!updatedAddons) return;

    // Update local state first
    setAddons(updatedAddons);
    setHasChanges(true);

    // Save to order via API
    await saveAdditionalChargesToOrder(updatedAddons, undefined, undefined);
  };

  const handleDeleteAddon = async (addonIndex) => {
    const updated = addons.filter((_, i) => i !== addonIndex);
    setAddons(updated);
    setHasChanges(true);
    await saveAdditionalChargesToOrder(updated, undefined, undefined);
  };

  // ── Products ─────────────────────────────────────────────
  const openProductsDrawer = () => {
    if (isServiceCompleted) return;
    setIsProductsDrawerOpen(true);
  };

  const closeProductsDrawer = () => setIsProductsDrawerOpen(false);

  const handleSaveProducts = async (updatedProducts) => {
    console.log("Products from ProductCatalog:", updatedProducts);
    setIsProductsDrawerOpen(false);

    if (!updatedProducts) return;

    const roundToTwo = (num) => {
      if (num === null || num === undefined || isNaN(num)) return 0;
      return Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;
    };

    const formattedProducts = updatedProducts.map((product) => {
      // Get values from product (using the correct field names from ProductCatalog)
      const effectivePrice = Number(product.sale_price_after_discount || product.effective_sale_price || product.price || 0);
      const mrpPrice = Number(product.mrp || product.mrp_price || product.sale_price || 0);
      const gstSlab = Number(product.gst_slab || product.tax_percentage || 0);
      const quantity = Number(product.quantity || 1);
      
      // ✅ CRITICAL: Calculate price without tax
      let priceWithoutTax = effectivePrice;
      let gstAmount = 0;
      
      if (gstSlab > 0 && effectivePrice > 0) {
        // priceWithoutTax = effectivePrice / (1 + gstSlab/100)
        priceWithoutTax = effectivePrice / (1 + gstSlab / 100);
        gstAmount = effectivePrice - priceWithoutTax;
      }
      
      return {
        // Preserve all original product data first
        ...product,

        // Core identifiers — these must come AFTER the spread to win
        product_id: product.id || product._id,
        product_name: product.name,

        // Quantity
        quantity: quantity,

        // ✅ CRITICAL: These fields MUST be correct
        unit_price: roundToTwo(effectivePrice),
        price: roundToTwo(priceWithoutTax),
        mrp_price: roundToTwo(mrpPrice),
        sale_price: roundToTwo(mrpPrice),
        total_price: roundToTwo(effectivePrice * quantity),
        
        // GST fields
        gst: roundToTwo(gstAmount),
        gstAmount: roundToTwo(gstAmount),
        gstAmountAfterDiscount: roundToTwo(gstAmount),
        tax_percentage: gstSlab,
        tax_amount: roundToTwo(gstAmount),
        
        // Amount breakdowns
        amount_without_tax: roundToTwo(priceWithoutTax),
        amount_with_tax: roundToTwo(effectivePrice),
        amount_before_discount: roundToTwo(mrpPrice),
        amount_after_discount: roundToTwo(effectivePrice),
        
        // Additional fields
        description: product.description || "",
        sku_number: product.sku_number || "",
        gst_slab: gstSlab,
      };
    });
    const formatted = updatedProducts.map((p) => {
      // Get the unit price from effective_sale_price or fallback to other fields
      const unitPrice = parseFloat(
        p.effective_sale_price ||
          p.unit_price ||
          p.price ||
          p.sale_price ||
          p.mrp ||
          0,
      );

      const quantity = p.quantity || 1;

      return {
        ...p,
        item_type: "product",
        product_id: p.id || p.product_id || p._id,
        product_name: p.name || p.product_name,
        quantity: quantity,
        unit_price: unitPrice,
        price: unitPrice,
        effective_sale_price: unitPrice,
        total_price: unitPrice * quantity,
        original_data: p,
      };
    });

    // Merge with existing products
    const merged = [...products];
    formattedProducts.forEach((item) => {
      const id = item.product_id || item._id;
      if (id) {
        const existingIndex = merged.findIndex(
          (existing) => (existing.product_id || existing._id) === id,
        );
        if (existingIndex !== -1) {
          const existing = merged[existingIndex];
          const newQty = (existing.quantity || 0) + (item.quantity || 1);
          const unitPrice = existing.unit_price || item.unit_price || 0;
          merged[existingIndex] = {
            ...existing,
            quantity: newQty,
            price: unitPrice,
            unit_price: unitPrice,
            effective_sale_price: unitPrice,
            total_price: unitPrice * newQty,
            amount_without_tax: item.amount_without_tax * newQty,
          };
        } else {
          merged.push(item);
        }
      } else {
        merged.push({
          ...item,
          product_id: `temp-${Date.now()}-${Math.random()}`,
        });
      }
    });

    setProducts(merged);
    setHasChanges(true);

    // Save to order via API
    await saveAdditionalChargesToOrder(undefined, merged, undefined);
  };

  const handleDeleteProduct = async (productIndex) => {
    const updated = products.filter((_, i) => i !== productIndex);
    setProducts(updated);
    setHasChanges(true);
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  const handleIncrementProduct = async (productIndex) => {
    const updated = [...products];
    const product = updated[productIndex];

    const unitPrice = parseFloat(
      product.effective_sale_price || product.unit_price || product.price || 0,
    );
    const newQuantity = (product.quantity || 1) + 1;

    updated[productIndex] = {
      ...product,
      quantity: newQuantity,
      total_price: unitPrice * newQuantity,
    };

    setProducts(updated);
    setHasChanges(true);
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  const handleDecrementProduct = async (productIndex) => {
    const updated = [...products];
    const product = updated[productIndex];

    const currentQty = product.quantity || 1;

    if (currentQty <= 1) {
      return;
    }

    const unitPrice = parseFloat(
      product.effective_sale_price || product.unit_price || product.price || 0,
    );
    const newQuantity = currentQty - 1;

    updated[productIndex] = {
      ...product,
      quantity: newQuantity,
      total_price: unitPrice * newQuantity,
    };

    setProducts(updated);
    setHasChanges(true);
    await saveAdditionalChargesToOrder(undefined, updated, undefined);
  };

  // ── Other Charges ────────────────────────────────────────
  const openOtherChargesDrawer = () => {
    if (isServiceCompleted) return;
    setIsOtherChargesDrawerOpen(true);
  };

  const closeOtherChargesDrawer = () => setIsOtherChargesDrawerOpen(false);

  const handleSaveOtherCharges = async (updatedCharges) => {
    setIsOtherChargesDrawerOpen(false);

    if (!updatedCharges) return;

    setOtherCharges(updatedCharges);
    setHasChanges(true);

    // Save to order via API
    await saveAdditionalChargesToOrder(undefined, undefined, updatedCharges);
  };

  const handleDeleteOtherCharge = async (chargeIndex) => {
    const updated = otherCharges.filter((_, i) => i !== chargeIndex);
    setOtherCharges(updated);
    setHasChanges(true);
    await saveAdditionalChargesToOrder(undefined, undefined, updated);
  };

  // ── Save additional charges to order ──────────────────────────────
  const saveAdditionalChargesToOrder = async (
    updatedAddons,
    updatedProducts,
    updatedCharges,
  ) => {
    if (!orderId || !vendorPetId) {
      console.warn(
        "Missing orderId or vendorPetId for saving additional charges",
      );
      return false;
    }

    try {
      // Find the current boarding order item
      const currentOrderItem =
        boardingOrderItem ||
        orderItems.find(
          (item) =>
            item?.vendor_pet_id === vendorPetId &&
            item?.item_type === "boarding",
        );

      if (!currentOrderItem) {
        console.warn("Boarding item not found in order");
        return false;
      }

      // Only patch extras on the order item. Do not send the processed
      // boarding row (dates, room, check-in flags, status) or vendororder
      // will rewrite the boarding/check-in record and drop the stay price.
      const payload = {
        order_id: orderId,
        vendor_client_id: clientId,
        order_type: orderType,
        order_items: [
          {
            ref_id: currentOrderItem.ref_id,
            item_type: currentOrderItem.item_type || "boarding",
            vendor_pet_id: currentOrderItem.vendor_pet_id || vendorPetId,
            addons: updatedAddons !== undefined ? updatedAddons : addons,
            products:
              updatedProducts !== undefined ? updatedProducts : products,
            otherCharges:
              updatedCharges !== undefined ? updatedCharges : otherCharges,
          },
        ],
      };

      // Call the API to update the order
      const response = await httpService.post(
        "/vendororder/addVendorOrderOrUpdate",
        payload,
      );

      if (response?.code === 200 || response?.status === 200) {
        // ✅ Refresh the order data
        await fetchOrder();

        // ✅ Force the header to re-render
        setHeaderRefreshKey((prev) => prev + 1);

        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving additional charges to order:", error);
      Swal.fire({
        title: "Error",
        text:
          error?.response?.data?.message || "Failed to save additional charges",
        icon: "error",
        confirmButtonText: "OK",
      });
      return false;
    }
  };

  const fetchBoardingActivities = async () => {
    if (!serviceRefId) {
      console.warn("No service_ref_id available for boarding activities");
      return;
    }

    try {
      setActivitiesLoading(true);
      setActivitiesError("");

      const response = await httpService.get(
        `/serviceactivity/boarding/${serviceRefId}/activities`,
      );

      const schedule = response?.data?.schedule || response?.schedule || [];
      const normalizedActivities = normalizeBoardingActivities(schedule);
      setActivities(normalizedActivities);
    } catch (error) {
      console.error("Error fetching boarding activities:", error);
      setActivities({});
      setActivitiesError(
        error?.response?.data?.message || "Unable to load boarding activities",
      );
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !serviceRefId) {
      return;
    }

    fetchBoardingActivities();
  }, [open, serviceRefId]);

  const handleOpenModal = (dateKey, timeSlot, task) => {
    const currentData = taskStatus[dateKey]?.[timeSlot]?.[task.id] || {};

    setActiveModal({
      dateKey,
      timeSlot,
      taskId: task.id,
      type: task.type,
      label: currentData.label !== undefined ? currentData.label : task.label,
      note: currentData.note !== undefined ? currentData.note : task.note,
      photos:
        currentData.photos !== undefined ? currentData.photos : task.photos,
      isChecked:
        currentData.checked !== undefined ? currentData.checked : task.checked,
    });
  };

  const handleCheckboxInteraction = (e, dateKey, timeSlot, task) => {
    e.preventDefault();
    const currentData = taskStatus[dateKey]?.[timeSlot]?.[task.id] || {};

    if (currentData.checked) {
      setTaskStatus((prev) => ({
        ...prev,
        [dateKey]: {
          ...(prev[dateKey] || {}),
          [timeSlot]: {
            ...(prev[dateKey]?.[timeSlot] || {}),
            [task.id]: { ...currentData, checked: false },
          },
        },
      }));
      setHasChanges(true);
    } else {
      handleOpenModal(dateKey, timeSlot, task);
    }
  };

  const handleSaveModal = (label, note, photos, markAsDone) => {
    if (!activeModal) return;

    const { dateKey, timeSlot, taskId, isChecked } = activeModal;
    const finalCheckedState = markAsDone !== undefined ? markAsDone : isChecked;

    setTaskStatus((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || {}),
        [timeSlot]: {
          ...(prev[dateKey]?.[timeSlot] || {}),
          [taskId]: {
            ...(prev[dateKey]?.[timeSlot]?.[taskId] || {}),
            label: label?.trim() || activeModal.label,
            checked: finalCheckedState,
            note,
            photos,
          },
        },
      },
    }));

    setHasChanges(true);
    setActiveModal(null);
  };

  const handleAddCustomActivity = (selectedDates, selectedSlots, taskData) => {
    let label = taskData.details || "Activity";
    const type = taskData.type;

    if (type === "feeding") {
      label = taskData.whatToFeed || "Feeding";
    } else if (type === "medication") {
      const parts = [taskData.medicineName];

      if (taskData.quantity) {
        parts.push(`Qty: ${taskData.quantity}`);
      }

      if (taskData.route) {
        const capitalizedRoute =
          taskData.route.charAt(0).toUpperCase() + taskData.route.slice(1);

        parts.push(`Route: ${capitalizedRoute}`);
      }

      if (taskData.instructions) {
        parts.push(taskData.instructions);
      }

      label = parts.join(" • ");
    } else if (type === "walking") {
      const walkDetails = [
        taskData.duration ? `${taskData.duration} min` : "",
        taskData.notes,
      ]
        .filter(Boolean)
        .join(" • ");

      label = walkDetails ? `Walking • ${walkDetails}` : "Walking";
    }

    const newTask = {
      id: `custom-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      label,
      type,
      note: "",
      photos: [],
      checked: false,
      raw_data: taskData,
      isNewCustomActivity: true,
    };

    setExtraTasks((prev) => {
      const newState = { ...prev };

      selectedDates.forEach((dateKey) => {
        const updatedDateTasks = {
          ...(newState[dateKey] || {}),
        };

        selectedSlots.forEach((slot) => {
          updatedDateTasks[slot] = [...(updatedDateTasks[slot] || []), newTask];
        });

        newState[dateKey] = updatedDateTasks;
      });

      return newState;
    });

    setHasChanges(true);
    setIsAddActivityModalOpen(false);
  };

  const handleClose = async () => {
    if (!hasChanges) {
      onClose?.();
      return;
    }

    const result = await Swal.fire({
      title: "Unsaved Changes",
      text: "You have unsaved changes. What would you like to do?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Save & Close",
      confirmButtonColor: "#4f46e5",
      cancelButtonText: "Close Anyway",
      cancelButtonColor: "#dc2626",
    });

    if (result.isConfirmed) {
      const saveSuccess = await handleSave();
      if (saveSuccess) {
        onClose?.();
      }
      return;
    }

    if (result.dismiss === Swal.DismissReason.cancel) {
      onClose?.();
    }
  };

  const handleComplete = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "All changes will be saved before completing this booking.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, complete it!",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setIsSaving(true);

      if (hasChanges) {
        const saveSuccess = await handleSave();

        if (!saveSuccess) {
          throw new Error("Failed to save changes");
        }
      }
      const completeResponse = await httpService.put(
        `/vendorbooking/updateVendorBookingStatus/${serviceRefId}`,
        {
          status: "completed",
        },
      );

      if (completeResponse?.code !== 200 && completeResponse?.status !== 200) {
        throw new Error(
          completeResponse?.message || "Failed to complete booking",
        );
      }

      setServiceStatus("completed");

      if(issingleserviceOrder) {
        onClose?.();
        onComplete?.(orderId);
      }else{
        onClose?.();
      }
    } catch (error) {
      console.error("Error completing booking:", error);
      Swal.fire({
        title: "Error",
        text: error?.message || "Failed to save changes or complete booking.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const updatePromises = [];
      const createPromises = [];

      for (const dateKey in extraTasks) {
        const slots = extraTasks[dateKey] || {};

        for (const timeSlot in slots) {
          const tasks = slots[timeSlot] || [];

          for (const task of tasks) {
            if (!task?.isNewCustomActivity) {
              continue;
            }

            const localTaskData =
              taskStatus[dateKey]?.[timeSlot]?.[task.id] || {};

            const description =
              localTaskData.label !== undefined
                ? localTaskData.label
                : task.label || "Activity";

            const notes =
              localTaskData.note !== undefined
                ? localTaskData.note
                : task.note || "";

            const photos =
              localTaskData.photos !== undefined
                ? localTaskData.photos
                : task.photos || [];

            const scheduledTaskType = task.type || "custom";

            const payload = {
              activity_type: "task",
              task_date: dateKey,
              time_slot: timeSlot.toLowerCase(),

              scheduled_task_type: scheduledTaskType,

              description: description.trim(),

              notes,
              is_completed: Boolean(localTaskData.checked),
              photos: Array.isArray(photos) ? photos : [],
            };

            if (task.raw_data) {
              payload.raw_data = task.raw_data;
            }

            console.log("Creating boarding activity:", payload);

            createPromises.push(
              httpService.post(
                `/serviceactivity/boarding/${serviceRefId}/activities`,
                payload,
              ),
            );
          }
        }
      }

      for (const dateKey in taskStatus) {
        const slots = taskStatus[dateKey] || {};

        for (const timeSlot in slots) {
          const tasks = slots[timeSlot] || {};

          for (const taskId in tasks) {
            const taskData = tasks[taskId];

            if (taskId.startsWith("custom-") || taskId.startsWith("new-")) {
              continue;
            }

            if (taskId.split("-").length > 2) {
              console.warn(
                "Skipping update for task with no valid backend activity id:",
                taskId,
              );
              continue;
            }

            const originalTask =
              activities[dateKey]?.[timeSlot]?.find((t) => t.id === taskId) ||
              extraTasks[dateKey]?.[timeSlot]?.find((t) => t.id === taskId) ||
              {};

            const finalNote =
              taskData.note !== undefined
                ? taskData.note
                : originalTask.note || "";

            const finalPhotos =
              taskData.photos !== undefined
                ? taskData.photos
                : originalTask.photos || [];

            const finalChecked =
              taskData.checked !== undefined
                ? taskData.checked
                : originalTask.checked || false;

            const finalLabel =
              taskData.label !== undefined
                ? taskData.label
                : originalTask.label || "";

            const payload = {
              notes: finalNote,
              description: finalLabel,
              photos: finalPhotos,
              is_completed: Boolean(finalChecked),
            };

            if (originalTask.originalActivity?.raw_data) {
              payload.raw_data = originalTask.originalActivity.raw_data;
            }

            console.log("Updating boarding activity:", taskId, payload);

            updatePromises.push(
              httpService.put(`/serviceactivity/activity/${taskId}`, payload),
            );
          }
        }
      }

      await Promise.all([...createPromises, ...updatePromises]);

      setTaskStatus({});
      setExtraTasks({});
      setHasChanges(false);
      await fetchBoardingActivities();
      await fetchOrder();
      setHeaderRefreshKey((prev) => prev + 1);
      return true;
    } catch (err) {
      console.error("Failed to save boarding activities:", err);

      console.error("API response:", err?.response?.data);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div
        className="fixed right-0 top-0 h-full z-50 flex flex-col bg-slate-50 shadow-2xl w-full md:w-[90vw] lg:w-[90vw] xl:w-[85vw] 2xl:w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <PetDetailsHeader
          key={headerRefreshKey}
          orderId={orderId}
          vendorPetId={vendorPetId}
          activeServiceRefId={serviceRefId}
          clientName={clientName}
          clientPhone={clientPhone}
          clientEmail={clientEmail}
          staffName={staffName}
          clientId={clientId}
          showOnlySelectedPet={true}
          onClose={handleClose}
        />

        <div className="flex-1 overflow-auto p-2 sm:p-2 space-y-2">
          <ServiceDetailsCard
            serviceCategory={serviceCategory}
            serviceDetails={serviceDetails}
            boardingOrderItem={boardingOrderItem}
            staffName={staffName}
            addons={addons}
            products={products}
            otherCharges={otherCharges}
            onOpenProductsDrawer={openProductsDrawer}
            onOpenAddonsDrawer={openAddonsDrawer}
            onOpenOtherChargesDrawer={openOtherChargesDrawer}
            onDeleteAddon={handleDeleteAddon}
            onDeleteProduct={handleDeleteProduct}
            onIncrementProduct={handleIncrementProduct}
            onDecrementProduct={handleDecrementProduct}
            onDeleteOtherCharge={handleDeleteOtherCharge}
            rawData={raw}
            orderId={orderId}
            clientId={clientId}
            clientName={clientName}
            clientPhone={clientPhone}
            vendorPetId={vendorPetId}
            readOnly={isServiceCompleted}
            onServiceUpdated={(updated) => {
              if (isServiceCompleted) return;
              setServiceDetails((prev) => ({
                ...prev,
                name: updated.service_name || prev.name,
                duration: updated.duration || prev.duration,
              }));

              setHasChanges(true);
            }}
          />

          {/* Top Header Section for the Table Area */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-slate-800">
              Daily Schedule
            </h2>

            {/* Dropdown Wrapper */}
            {!isServiceCompleted && (
            <div className="relative">
              <button
                onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-colors shadow-sm"
              >
                <PlusIcon className="w-4 h-4" /> Add Activity
              </button>

              {/* Dropdown Menu Overlay and Content */}
              {isAddMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsAddMenuOpen(false)}
                  />

                  <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1.5 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {["Feeding", "Walking", "Medication"].map((option) => (
                      <button
                        key={option}
                        onClick={() => {
                          setAddActivityType(option.toLowerCase());
                          setIsAddActivityModalOpen(true);
                          setIsAddMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                      >
                        {option}
                      </button>
                    ))}

                    <div className="h-px bg-slate-100 my-1 mx-2" />

                    <button
                      onClick={() => {
                        setAddActivityType("custom");
                        setIsAddActivityModalOpen(true);
                        setIsAddMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                    >
                      Custom
                    </button>
                  </div>
                </>
              )}
            </div>
            )}
          </div>

          {activitiesLoading && (
            <div className="mb-3 rounded-lg bg-blue-50 border border-blue-100 px-3 py-2 text-xs text-blue-700">
              Loading boarding activities...
            </div>
          )}

          {activitiesError && (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
              {activitiesError}
            </div>
          )}

          <div className={`bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden ${isServiceCompleted ? "pointer-events-none opacity-90" : ""}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-max">
                <thead>
                  <tr>
                    <th className="border-b border-r border-slate-200 bg-slate-50 p-4 sticky left-0 z-10 w-32 shadow-[1px_0_0_0_#e2e8f0] font-semibold text-slate-700">
                      Date
                    </th>
                    {TIME_SLOTS.map((slot) => (
                      <th
                        key={slot}
                        className="border-b border-r border-slate-200 bg-slate-50 p-3 text-center font-semibold text-slate-700 min-w-[320px] capitalize"
                      >
                        {slot}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dateTabs.map((date) => {
                    const dateKey = getDateKey(date);
                    const today = new Date();
                    const currentHour = today.getHours();

                    today.setHours(0, 0, 0, 0);
                    const rowDate = new Date(date);
                    rowDate.setHours(0, 0, 0, 0);

                    const isPastDate = rowDate < today;
                    const isToday = rowDate.getTime() === today.getTime();
                    const isFutureDate = rowDate > today;

                    return (
                      <tr key={dateKey} className="group">
                        <td className="border-b border-r border-slate-200 bg-slate-50 p-4 font-medium text-slate-700 sticky left-0 z-10 shadow-[1px_0_0_0_#e2e8f0] whitespace-nowrap">
                          {date.getDate()}{" "}
                          {date.toLocaleString("en-GB", { month: "short" })}
                        </td>

                        {TIME_SLOTS.map((slot) => {
                          let isSlotPassed = false;

                          if (isToday) {
                            if (slot === "Morning" && currentHour >= 12)
                              isSlotPassed = true;
                            if (slot === "Afternoon" && currentHour >= 17)
                              isSlotPassed = true;
                            if (slot === "Evening" && currentHour >= 21)
                              isSlotPassed = true;
                          }

                          const cellBookings = relatedBookings.filter(
                            (booking) =>
                              booking.dateKey === dateKey &&
                              booking.timeSlot === slot,
                          );

                          // COMBINING ALL TASKS
                          const apiTasks = activities[dateKey]?.[slot] || [];
                          const unsortedTasks = [
                            ...apiTasks,
                            ...(extraTasks[dateKey]?.[slot] || []),
                          ];

                          const cellTasks = [...unsortedTasks].sort((a, b) => {
                            const typeA = a.type?.toLowerCase() || "custom";
                            const typeB = b.type?.toLowerCase() || "custom";
                            const orderA = ACTIVITY_TYPE_ORDER[typeA] || 99;
                            const orderB = ACTIVITY_TYPE_ORDER[typeB] || 99;
                            return orderA - orderB;
                          });

                          return (
                            <td
                              key={slot}
                              className="border-b border-r border-slate-200 p-4 align-top hover:bg-slate-50/50 transition-colors"
                            >
                              <div className="flex flex-col gap-4">
                                {cellBookings.map((booking) => {
                                  const isCompleted =
                                    isRelatedBookingCompleted(booking);

                                  return (
                                    <div
                                      key={booking.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() =>
                                        handleOpenRelatedBooking(booking)
                                      }
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "Enter" ||
                                          e.key === " "
                                        ) {
                                          e.preventDefault();
                                          handleOpenRelatedBooking(booking);
                                        }
                                      }}
                                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                                        isCompleted
                                          ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70"
                                          : "border-indigo-400 bg-indigo-50/60 hover:bg-indigo-100/70"
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faPaw}
                                        className={`text-xs shrink-0 ${
                                          isCompleted
                                            ? "text-emerald-600"
                                            : "text-indigo-500"
                                        }`}
                                      />

                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <span
                                            className={`text-[11px] uppercase tracking-wide font-bold shrink-0 ${
                                              isCompleted
                                                ? "text-emerald-700"
                                                : "text-indigo-600"
                                            }`}
                                          >
                                            {booking.type === "grooming"
                                              ? "Grooming"
                                              : booking.type === "vet" ||
                                                  booking.type ===
                                                    "vet_clinic" ||
                                                  booking.type === "veterinary"
                                                ? "Vet Clinic"
                                                : String(
                                                    booking.type || "Service",
                                                  )
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (char) =>
                                                      char.toUpperCase(),
                                                    )}
                                          </span>

                                          <span
                                            className={`text-sm font-medium truncate ${
                                              isCompleted
                                                ? "text-emerald-800"
                                                : "text-slate-700"
                                            }`}
                                          ></span>
                                        </div>

                                        <div
                                          className={`text-[11px] mt-0.5 ${
                                            isCompleted
                                              ? "text-emerald-600"
                                              : "text-slate-500"
                                          }`}
                                        ></div>
                                      </div>

                                      {/* Completed indicator */}
                                      {isCompleted && (
                                        <div className="shrink-0 flex items-center gap-1 text-emerald-600">
                                          <CheckCircleIcon className="w-4 h-4" />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {cellTasks.map((task) => {
                                  const taskData =
                                    taskStatus[dateKey]?.[slot]?.[task.id] ||
                                    {};

                                  const isChecked =
                                    taskData.checked !== undefined
                                      ? taskData.checked
                                      : task.checked;
                                  const currentNote =
                                    taskData.note !== undefined
                                      ? taskData.note
                                      : task.note;
                                  const currentPhotos =
                                    taskData.photos !== undefined
                                      ? taskData.photos
                                      : task.photos;
                                  const displayLabel =
                                    taskData.label !== undefined
                                      ? taskData.label
                                      : task.label;

                                  const hasNote = !!currentNote;
                                  const photoCount = currentPhotos?.length || 0;
                                  const maxChars =
                                    hasNote || photoCount > 0 ? 25 : 45;

                                  let textColorClass =
                                    "text-slate-600 group-hover/item:text-slate-800";

                                  if (isChecked) {
                                    textColorClass =
                                      "text-emerald-600 font-medium";
                                  } else if (isPastDate) {
                                    textColorClass = "text-red-500 font-medium";
                                  } else if (isToday && isSlotPassed) {
                                    textColorClass =
                                      "text-amber-500 font-medium";
                                  } else if (isFutureDate) {
                                    textColorClass = "text-slate-400";
                                  }

                                  return (
                                    <div
                                      key={task.id}
                                      className="flex items-center gap-3 group/item"
                                    >
                                      <div
                                        className="shrink-0 relative flex items-center justify-center w-[18px] h-[18px]"
                                        onClick={(e) => {
                                          if (isFutureDate) return;
                                          handleCheckboxInteraction(
                                            e,
                                            dateKey,
                                            slot,
                                            task,
                                          );
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          readOnly
                                          disabled={isFutureDate}
                                          checked={isChecked}
                                          className={`peer appearance-none w-[18px] h-[18px] border-2 border-slate-300 rounded-full checked:bg-indigo-600 checked:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 transition-all ${
                                            isFutureDate
                                              ? "cursor-not-allowed opacity-50 bg-slate-50"
                                              : "cursor-pointer"
                                          }`}
                                        />

                                        <svg
                                          className="absolute w-3 h-3 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          strokeWidth="3.5"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      </div>

                                      <div className="flex items-center flex-wrap gap-2.5 w-full">
                                        <div className="flex flex-col min-w-0">
                                          {(() => {
                                            const displayLabel =
                                              taskData.label ||
                                              task.label ||
                                              "";
                                            return (
                                              <span
                                                onClick={(e) => {
                                                  if (isFutureDate) return;
                                                  handleCheckboxInteraction(
                                                    e,
                                                    dateKey,
                                                    slot,
                                                    task,
                                                  );
                                                }}
                                                title={displayLabel}
                                                className={`text-sm select-none transition-colors ${textColorClass} ${
                                                  isFutureDate
                                                    ? "cursor-not-allowed"
                                                    : "cursor-pointer"
                                                }`}
                                              >
                                                <FontAwesomeIcon
                                                  icon={getActivityIcon(
                                                    task.type,
                                                  )}
                                                  className="mr-2 inline-block opacity-75"
                                                />
                                                {displayLabel.length > maxChars
                                                  ? `${displayLabel.substring(0, maxChars)}...`
                                                  : displayLabel}
                                              </span>
                                            );
                                          })()}

                                          {task.details && (
                                            <span className="text-[11px] text-slate-500 mt-0.5">
                                              {task.details}
                                            </span>
                                          )}
                                        </div>

                                        {hasNote && (
                                          <button
                                            onClick={() => {
                                              if (isFutureDate) return;
                                              handleOpenModal(
                                                dateKey,
                                                slot,
                                                task,
                                              );
                                            }}
                                            className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded font-medium border bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 transition-colors"
                                          >
                                            <DocumentTextIcon className="w-3 h-3" />
                                            Note
                                          </button>
                                        )}

                                        {photoCount > 0 && (
                                          <button
                                            onClick={() => {
                                              if (isFutureDate) return;
                                              handleOpenModal(
                                                dateKey,
                                                slot,
                                                task,
                                              );
                                            }}
                                            className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded font-medium border bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100 transition-colors"
                                          >
                                            <PhotoIcon className="w-3 h-3" />
                                            {photoCount} Photo
                                            {photoCount > 1 ? "s" : ""}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {hasChanges ? (
              <span className="flex items-center gap-1.5 text-[11px] text-amber-600 font-semibold">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />{" "}
                Unsaved changes
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-[11px] text-emerald-600 font-semibold">
                <CheckCircleIcon className="w-3.5 h-3.5" /> All saved
              </span>
            )}
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={handleClose}
              className="px-4 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
            >
              Close
            </button>
            {!isServiceCompleted ? (
              <button
                onClick={handleComplete}
                className="px-4 py-2 border border-slate-200 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
              >
                Complete
              </button>
            ) : (
              <button
                disabled
                className="px-4 py-2 bg-slate-200 text-slate-500 rounded-xl text-[12px] font-semibold cursor-not-allowed"
              >
                Completed
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges || isServiceCompleted}
              className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-[12px] font-bold hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {activeModal && (
        <TaskDetailsModal
          activeModal={activeModal}
          onClose={() => setActiveModal(null)}
          onSave={handleSaveModal}
        />
      )}

      {isAddActivityModalOpen && (
        <AddActivityModal
          isOpen={isAddActivityModalOpen}
          activityType={addActivityType}
          onClose={() => setIsAddActivityModalOpen(false)}
          onAdd={handleAddCustomActivity}
          dateTabs={dateTabs}
          getDateKey={getDateKey}
        />
      )}
      {/* Add-ons drawer */}
      {isAddonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={isAddonsDrawerOpen}
          onClose={closeAddonsDrawer}
          onSave={handleSaveAddons}
          serviceCategoryId={serviceCategoryTypeId}
          petType={raw.pet_type || booking?.pet?.type || "Both"}
          petTypeId={raw.pet_type_id}
          selectedAddons={addons}
          serviceTypeName={serviceDetails.name}
        />
      )}

      {/* Products drawer */}
      {isProductsDrawerOpen && (
        <ProductCatalog
          isOpen={isProductsDrawerOpen}
          onClose={closeProductsDrawer}
          onCartUpdate={handleSaveProducts}
          apiEndpoint="/vendorproduct/getAvailableInventoryProducts"
        />
      )}

      {/* Other Charges drawer */}
      {isOtherChargesDrawerOpen && (
        <OtherChargesDrawer
          isOpen={isOtherChargesDrawerOpen}
          onClose={closeOtherChargesDrawer}
          onSave={handleSaveOtherCharges}
          initialCharges={otherCharges}
        />
      )}
      {/* Grooming booking drawer */}
      {isGroomingDrawerOpen && selectedRelatedBooking && (
        <GroomingStartedDrawer
          open={isGroomingDrawerOpen}
          booking={getRelatedDrawerBooking(selectedRelatedBooking)}
          appointmentRow={selectedRelatedBooking.originalItem}
          onClose={handleCloseGroomingDrawer}
          onSave={() => {
            // Grooming drawer handles its own save.
          }}
          onComplete={() => {
            if (!selectedRelatedBooking?.originalItem?._id) {
              return;
            }

            const completedId = selectedRelatedBooking.originalItem._id;

            setOrderItems((prev) =>
              prev.map((item) =>
                item._id === completedId
                  ? {
                      ...item,
                      status: "completed",
                    }
                  : item,
              ),
            );
          }}
        />
      )}

      {/* Vet Clinic booking drawer */}
      {isVetDrawerOpen && selectedRelatedBooking && (
        <VetClinicStartedDrawer
          open={isVetDrawerOpen}
          booking={getRelatedDrawerBooking(selectedRelatedBooking)}
          appointmentRow={selectedRelatedBooking.originalItem}
          onClose={handleCloseVetDrawer}
          onComplete={() => {
            if (selectedRelatedBooking?.originalItem) {
              const completedId = selectedRelatedBooking.originalItem._id;

              setOrderItems((prev) =>
                prev.map((item) =>
                  item._id === completedId
                    ? { ...item, status: "completed" }
                    : item,
                ),
              );
            }

            handleCloseVetDrawer();
          }}
        />
      )}
    </>
  );
};

// Modal Component for Adding Activities Dynamically Based on Type
const AddActivityModal = ({
  isOpen,
  activityType,
  onClose,
  onAdd,
  dateTabs,
  getDateKey,
}) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([TIME_SLOTS[0]]);

  // States for varying inputs
  const [details, setDetails] = useState("");
  const [whatToFeed, setWhatToFeed] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [medicineName, setMedicineName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [route, setRoute] = useState("oral");
  const [instructions, setInstructions] = useState("");

  // Initialize with the first date and first slot on open
  useEffect(() => {
    if (isOpen && dateTabs.length > 0) {
      setSelectedDates([getDateKey(dateTabs[0])]);
      setSelectedSlots([TIME_SLOTS[0]]);

      // Reset form states
      setDetails("");
      setWhatToFeed("");
      setDuration("");
      setNotes("");
      setMedicineName("");
      setQuantity("");
      setRoute("oral");
      setInstructions("");
    }
  }, [isOpen]);

  // Toggle Time Slots
  const toggleSlot = (slot) => {
    setSelectedSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot],
    );
  };

  // Date Selection Handlers
  const handleAddDate = (e) => {
    const val = e.target.value;
    if (val && !selectedDates.includes(val)) {
      setSelectedDates([...selectedDates, val]);
    }
  };

  const handleRemoveDate = (dateToRemove) => {
    setSelectedDates(selectedDates.filter((d) => d !== dateToRemove));
  };

  const handleSelectAllDates = () => {
    setSelectedDates(dateTabs.map((d) => getDateKey(d)));
  };

  // Helper to format date strings for the chips
  const formatChipDate = (dStr) => {
    const [year, month, day] = dStr.split("-");
    const d = new Date(year, month - 1, day);
    return `${d.getDate()} ${d.toLocaleString("en-GB", { month: "short" })}`;
  };

  // Helper for Modal Title
  const getModalTitle = () => {
    if (activityType === "custom") return "Add New Activity";
    return `Add ${activityType.charAt(0).toUpperCase() + activityType.slice(1)} Activity`;
  };

  // Form validation handler
  const isFormValid = () => {
    if (selectedDates.length === 0 || selectedSlots.length === 0) return false;

    if (activityType === "feeding") return !!whatToFeed.trim();
    if (activityType === "walking") return !!duration.trim() || !!notes.trim();
    if (activityType === "medication") return !!medicineName.trim();
    return !!details.trim(); // Custom
  };

  const submitActivity = () => {
    if (!isFormValid()) return;

    const taskData = { type: activityType };

    if (activityType === "feeding") {
      taskData.whatToFeed = whatToFeed.trim();
    } else if (activityType === "walking") {
      taskData.duration = duration.trim();
      taskData.notes = notes.trim();
    } else if (activityType === "medication") {
      taskData.medicineName = medicineName.trim();
      taskData.quantity = quantity.trim();
      taskData.route = route;
      taskData.instructions = instructions.trim();
    } else {
      taskData.details = details.trim();
    }

    onAdd(selectedDates, selectedSlots, taskData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <FontAwesomeIcon
              icon={getActivityIcon(activityType)}
              className="text-slate-500 w-4 h-4"
            />
            {getModalTitle()}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5 overflow-y-auto max-h-[70vh]">
          {/* DATES SECTION */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">
                Dates
              </label>
              <button
                type="button"
                onClick={handleSelectAllDates}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2 py-1 rounded-md transition-colors"
              >
                Select All
              </button>
            </div>

            <select
              onChange={handleAddDate}
              value=""
              className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
            >
              <option value="" disabled>
                Select dates to add...
              </option>
              {dateTabs.map((d) => {
                const val = getDateKey(d);
                if (selectedDates.includes(val)) return null;
                return (
                  <option key={val} value={val}>
                    {d.getDate()}{" "}
                    {d.toLocaleString("en-GB", { month: "short" })}{" "}
                    {d.getFullYear()}
                  </option>
                );
              })}
            </select>

            {selectedDates.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {selectedDates.map((dateKey) => (
                  <span
                    key={dateKey}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-[13px] font-medium border border-indigo-100"
                  >
                    {formatChipDate(dateKey)}
                    <button
                      type="button"
                      onClick={() => handleRemoveDate(dateKey)}
                      className="p-0.5 hover:bg-indigo-200 rounded-md transition-colors"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* TIME SLOTS SECTION */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Time Slots
            </label>
            <div className="flex items-center flex-wrap gap-4">
              {TIME_SLOTS.map((slot) => (
                <label
                  key={slot}
                  className="flex items-center gap-2 cursor-pointer group"
                >
                  <input
                    type="checkbox"
                    checked={selectedSlots.includes(slot)}
                    onChange={() => toggleSlot(slot)}
                    className="w-4 h-4 text-indigo-600 bg-white border-slate-300 rounded focus:ring-indigo-500/30 transition-colors cursor-pointer"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800 select-none transition-colors">
                    {slot}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* DYNAMIC ACTIVITY DETAILS SECTION */}

          {activityType === "feeding" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">
                Feeding Details
              </label>
              <input
                type="text"
                value={whatToFeed}
                onChange={(e) => setWhatToFeed(e.target.value)}
                placeholder="What to feed..."
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          )}

          {activityType === "walking" && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="e.g. 15"
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Notes
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Needs to walk slow"
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
            </>
          )}

          {activityType === "medication" && (
            <>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Medicine Name *
                </label>
                <input
                  type="text"
                  value={medicineName}
                  onChange={(e) => setMedicineName(e.target.value)}
                  placeholder="e.g. Amoxicillin"
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
              <div className="flex gap-3">
                <div className="space-y-1.5 flex-1">
                  <label className="text-sm font-medium text-slate-700">
                    Quantity
                  </label>
                  <input
                    type="text"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="e.g. 1 pill"
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>
                <div className="space-y-1.5 flex-1">
                  <label className="text-sm font-medium text-slate-700">
                    Route
                  </label>
                  <select
                    value={route}
                    onChange={(e) => setRoute(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 bg-white"
                  >
                    <option value="oral">Oral</option>
                    <option value="topical">Topical</option>
                    <option value="injection">Injection</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  Instructions
                </label>
                <input
                  type="text"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="e.g. Give with food"
                  className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
            </>
          )}

          {activityType === "custom" && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-slate-700">
                Activity Details
              </label>
              <input
                type="text"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="e.g. Extra playtime"
                className="w-full border border-slate-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              />
            </div>
          )}
        </div>

        {/* FOOTER ACTIONS */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200/50 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={submitActivity}
            disabled={!isFormValid()}
            className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-xl shadow-sm transition-colors"
          >
            Add Activity
          </button>
        </div>
      </div>
    </div>
  );
};

// Task Details Modal Component
const TaskDetailsModal = ({ activeModal, onClose, onSave }) => {
  const [activityLabel, setActivityLabel] = useState(activeModal.label || "");
  const [isEditingActivity, setIsEditingActivity] = useState(false);
  const [note, setNote] = useState(activeModal.note || "");
  const [photos, setPhotos] = useState(activeModal.photos || []);

  const activityInputRef = useRef(null);

  const handleEditActivity = () => {
    setIsEditingActivity(true);
    setTimeout(() => {
      if (activityInputRef.current) {
        activityInputRef.current.style.height = "auto";
        activityInputRef.current.style.height = `${activityInputRef.current.scrollHeight}px`;
        activityInputRef.current.focus();
        activityInputRef.current.selectionStart =
          activityInputRef.current.value.length;
        activityInputRef.current.selectionEnd =
          activityInputRef.current.value.length;
      }
    }, 0);
  };

  const handleCancelEditActivity = () => {
    setActivityLabel(activeModal.label || "");
    setIsEditingActivity(false);
  };

  const handleSaveActivityName = () => {
    const trimmedLabel = activityLabel.trim();
    if (!trimmedLabel) return;
    setActivityLabel(trimmedLabel);
    setIsEditingActivity(false);
  };

  const handleUploadComplete = (uploadedUrls) => {
    setPhotos(uploadedUrls);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100 gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="pt-0.5 text-slate-500 shrink-0">
              <FontAwesomeIcon
                icon={getActivityIcon(activeModal.type)}
                className="w-5 h-5"
              />
            </div>

            <div className="flex-1 min-w-0">
              {isEditingActivity ? (
                <textarea
                  ref={activityInputRef}
                  value={activityLabel}
                  onChange={(e) => {
                    setActivityLabel(e.target.value);
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  autoFocus
                  rows={1}
                  className="w-full border border-indigo-300 rounded-lg px-3 py-1.5 text-base font-semibold leading-6 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none overflow-hidden"
                  onKeyDown={(e) => {
                    if (e.key === "Escape") handleCancelEditActivity();
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSaveActivityName();
                    }
                  }}
                />
              ) : (
                <h3
                  className="text-base font-semibold leading-6 text-slate-800 whitespace-normal break-words"
                  title={activityLabel}
                >
                  {activityLabel}
                </h3>
              )}
            </div>

            <div className="flex items-start gap-1.5 shrink-0 pt-0.5">
              {!isEditingActivity && (
                <button
                  type="button"
                  onClick={handleEditActivity}
                  className="px-2.5 py-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="shrink-0 p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-5 overflow-y-auto">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-slate-700">
              Add Note
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="How did it go?"
              className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 resize-none min-h-[80px]"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-700">
                Photos ({photos.length})
              </label>
            </div>

            <FileUpload
              existingUrls={photos}
              onUploadComplete={handleUploadComplete}
              multiple={true}
              maxFiles={5}
              moduleName="serviceactivity"
              docType="activity"
            />
          </div>
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200/50 rounded-xl"
          >
            Cancel
          </button>

          <button
            onClick={() =>
              onSave(activityLabel, note, photos, activeModal.isChecked)
            }
            className="px-4 py-2 text-sm font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors"
          >
            Save
          </button>

          {!activeModal.isChecked && (
            <button
              onClick={() => onSave(activityLabel, note, photos, true)}
              className="px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition-colors"
            >
              Mark as Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardingStartedDrawer;
